import React from 'react';
import styled, { css } from 'styled-components';
import { system, extractSysProps } from './system';
import { spinKeyframes } from './Spinner';
import Icon from './Icon';

// molekule's Button composed ONLY styled-system `space` — other style props
// (minWidth, width, …) were ignored at runtime in production. Replicate that:
// space props become CSS, all other known style props are dropped entirely.
const BUTTON_SPACE_KEYS: ReadonlySet<string> = new Set([
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
]);

type SysProps = Partial<Record<string, any>>;

// Fallback size table if the theme lacks sizes.Button (createTheme always
// provides it; this guards bare-ThemeProvider usage in tests)
const SIZE_FALLBACK: Record<
  string,
  { fontSize: number; height: number; padding: string }
> = {
  sm: { fontSize: 16, height: 32, padding: '0 8px' },
  md: { fontSize: 16, height: 40, padding: '0 12px' },
  lg: { fontSize: 16, height: 48, padding: '0 16px' },
};

function sizeStyles(p: { theme?: any; $size?: string }): {
  fontSize: number;
  height: number;
  padding: string;
} {
  const size = p.$size || 'md';
  return (
    (p.theme && p.theme.sizes && p.theme.sizes.Button && p.theme.sizes.Button[size]) ||
    SIZE_FALLBACK[size] ||
    SIZE_FALLBACK.md
  );
}

interface StyledButtonInnerProps {
  $sys?: Record<string, any>;
  $variant?: string;
  $size?: string;
  $block?: boolean;
  $disabled?: boolean;
  $isLoading?: boolean;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
  $hasText?: boolean;
  $colorFocus?: string;
  $borderRadius?: number;
}

const StyledButton = styled.button<StyledButtonInnerProps>`
  position: relative;
  display: inline-flex;
  cursor: pointer;
  text-transform: capitalize;
  text-align: center;
  text-decoration: none;
  font-family: inherit;
  font-weight: bold;
  appearance: none;
  border-radius: ${(p) => (p.$borderRadius != null ? p.$borderRadius : p.theme && p.theme.radius != null ? p.theme.radius : 8)}px;
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
  width: ${(p) => (p.$block ? '100%' : 'auto')};
  border: 1px solid transparent;
  transition: 175ms;
  white-space: nowrap;
  user-select: none;
  outline: none;
  justify-content: center;
  align-items: center;
  font-size: ${(p) => sizeStyles(p).fontSize}px;
  height: ${(p) => sizeStyles(p).height}px;
  padding: ${(p) => sizeStyles(p).padding};

  &::before {
    transition: opacity 250ms;
    content: '';
    position: absolute;
    left: -5px;
    top: -5px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    z-index: 0;
    opacity: 0;
    border: 4px solid
      ${(p) => p.$colorFocus || (p.theme && p.theme.colors && p.theme.colors.colorFocus) || 'rgba(0,0,0,0.2)'};
    border-radius: ${(p) => (p.$borderRadius != null ? p.$borderRadius : p.theme && p.theme.radius != null ? p.theme.radius : 8) + 4}px;
  }

  ${(p) =>
    p.$hasLeftIcon &&
    p.$hasText &&
    css`
      i {
        padding-right: 4px;
      }
    `}
  ${(p) =>
    p.$hasRightIcon &&
    p.$hasText &&
    css`
      i {
        padding-left: 4px;
      }
    `}

  ${(p) => {
    if (!p.$isLoading) return '';
    const h = sizeStyles(p).height;
    const color =
      getVariantStyles(p.theme, p.$variant || 'primary').color || '#fff';
    return css`
        color: transparent !important;
        pointer-events: none;
        position: relative;
        opacity: 0.75;
        &::after {
          display: block;
          content: '';
          animation: ${spinKeyframes} 820ms infinite linear;
          border-radius: 100%;
          border: 2px solid ${color};
          border-right-color: transparent;
          border-top-color: transparent;
          left: 50%;
          margin-left: -${h * 0.25}px;
          margin-top: -${h * 0.25}px;
          position: absolute;
          top: 48%;
          height: ${h * 0.5}px;
          width: ${h * 0.5}px;
        }
      `;
  }}

  &[disabled] {
    pointer-events: none;
    opacity: 0.75;
  }

  &:focus {
    &::before {
      opacity: 1;
    }
  }

  ${(p) => system(p.$sys, p.theme)}
`;

function getVariantStyles(theme: any, variant: string): Record<string, any> {
  // Try theme.variants.Button[variant] (molekule path)
  const fromVariants =
    theme &&
    theme.variants &&
    theme.variants.Button &&
    theme.variants.Button[variant];
  if (fromVariants) return fromVariants;
  // Try theme.components.Button.variants[variant] (ui-core path)
  const fromComponents =
    theme &&
    theme.components &&
    theme.components.Button &&
    theme.components.Button.variants &&
    theme.components.Button.variants[variant];
  if (fromComponents) return fromComponents;
  return {};
}

export interface ButtonProps
  extends
    SysProps,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: string;
  size?: string;
  block?: boolean;
  loading?: boolean;
  leftIcon?: string | React.ComponentType<any>;
  leftIconProps?: Record<string, any>;
  rightIcon?: string | React.ComponentType<any>;
  rightIconProps?: Record<string, any>;
  colorFocus?: string;
  borderRadius?: number;
  children?: React.ReactNode;
}

function renderIconProp(
  icon: string | React.ComponentType<any>,
  iconProps?: Record<string, any>,
) {
  if (typeof icon === 'string') {
    return <Icon name={icon} {...(iconProps || {})} />;
  }
  const IconComponent = icon;
  return <IconComponent {...(iconProps || {})} />;
}

// We need a version that can access the theme for variant resolution.
// Use a thin wrapper that reads variant styles from the theme via a render prop pattern.
const StyledButtonWithVariant = styled(StyledButton)`
  ${(p) => {
    const variantStyles = getVariantStyles(p.theme, p.$variant || 'primary');
    const parts: string[] = [];
    if (variantStyles.backgroundColor)
      parts.push(`background-color: ${variantStyles.backgroundColor};`);
    if (variantStyles.color) parts.push(`color: ${variantStyles.color};`);
    if (variantStyles.borderColor)
      parts.push(`border-color: ${variantStyles.borderColor};`);
    if (variantStyles['&:hover']) {
      const h = variantStyles['&:hover'];
      const hp: string[] = [];
      if (h.backgroundColor) hp.push(`background-color: ${h.backgroundColor};`);
      if (h.color) hp.push(`color: ${h.color};`);
      if (h.borderColor) hp.push(`border-color: ${h.borderColor};`);
      if (hp.length) parts.push(`&:hover { ${hp.join(' ')} }`);
    }
    if (variantStyles['&:active']) {
      const a = variantStyles['&:active'];
      const ap: string[] = [];
      if (a.backgroundColor) ap.push(`background-color: ${a.backgroundColor};`);
      if (a.color) ap.push(`color: ${a.color};`);
      if (a.borderColor) ap.push(`border-color: ${a.borderColor};`);
      if (ap.length) parts.push(`&:active { ${ap.join(' ')} }`);
    }
    const disabledBlock =
      variantStyles['&[disabled]'] || variantStyles['&:disabled'];
    if (disabledBlock) {
      const dp: string[] = [];
      if (disabledBlock.backgroundColor)
        dp.push(`background-color: ${disabledBlock.backgroundColor};`);
      if (disabledBlock.color) dp.push(`color: ${disabledBlock.color};`);
      if (disabledBlock.borderColor)
        dp.push(`border-color: ${disabledBlock.borderColor};`);
      if (dp.length) parts.push(`&[disabled], &:disabled { ${dp.join(' ')} }`);
    }
    return parts.join('\n');
  }}
`;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      block = false,
      loading = false,
      leftIcon,
      leftIconProps,
      rightIcon,
      rightIconProps,
      colorFocus,
      borderRadius,
      children,
      disabled,
      ...remaining
    } = props;

    const { sys, rest } = extractSysProps(
      remaining as Record<string, any>,
      BUTTON_SPACE_KEYS,
    );
    const hasText = !!children;

    return (
      <StyledButtonWithVariant
        ref={ref}
        $variant={variant}
        $sys={sys}
        $block={block}
        $disabled={!!disabled}
        $isLoading={loading}
        $hasLeftIcon={!!leftIcon}
        $hasRightIcon={!!rightIcon}
        $hasText={hasText}
        $colorFocus={colorFocus}
        $borderRadius={borderRadius}
        $size={size}
        disabled={disabled || loading}
        {...rest}
      >
        {leftIcon && renderIconProp(leftIcon, leftIconProps)}
        {children}
        {rightIcon && renderIconProp(rightIcon, rightIconProps)}
      </StyledButtonWithVariant>
    );
  },
);

// Button.Group — wraps buttons in a flex row; based on molekule Button.Group
interface ButtonGroupProps
  extends SysProps, Omit<React.HTMLAttributes<HTMLDivElement>, keyof SysProps> {
  connected?: boolean;
  borderRadius?: number;
  children?: React.ReactNode;
}

interface StyledGroupInnerProps {
  $sys?: Record<string, any>;
  $connected?: boolean;
  $borderRadius?: number;
}

const StyledButtonGroup = styled.div<StyledGroupInnerProps>`
  display: flex;
  align-items: center;

  & > button:not(:first-child) {
    margin-left: 1rem;
  }

  ${(p) =>
    p.$connected &&
    css`
      & > button:not(:first-child) {
        margin-left: 0;
      }
      & > button:first-child {
        border-radius: ${p.$borderRadius != null ? p.$borderRadius : 8}px 0 0
          ${p.$borderRadius != null ? p.$borderRadius : 8}px;
      }
      & > button:last-child {
        border-radius: 0 ${p.$borderRadius != null ? p.$borderRadius : 8}px
          ${p.$borderRadius != null ? p.$borderRadius : 8}px 0;
      }
      & > :not(:first-child):not(:last-child) {
        border-radius: 0;
      }
    `}

  ${(p) => system(p.$sys, p.theme)}
`;

const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  function ButtonGroup(props, ref) {
    const { connected, borderRadius, ...remaining } = props;
    const { sys, rest } = extractSysProps(remaining as Record<string, any>);
    return (
      <StyledButtonGroup
        ref={ref}
        $sys={sys}
        $connected={!!connected}
        $borderRadius={borderRadius}
        {...rest}
      />
    );
  },
);

(Button as any).Group = ButtonGroup;

export default Button as typeof Button & { Group: typeof ButtonGroup };
